import '@asyncapi/react-component/styles/default.min.css';
import AsyncApiComponent from '@asyncapi/react-component';
import type { ConfigInterface } from '@asyncapi/react-component';
import { ErrorBoundary } from '@backstage/core-components';
import { IAsyncApiSpecification } from '@internal/schemas-common';
import { useStyles } from './AsyncApiViewer.styles';
import { useEffect, useRef } from 'react';

const DEFAULT_CONFIG: ConfigInterface = {
  show: {
    sidebar: false,
    info: false,
    servers: false,
    operations: true,
    messages: true,
    schemas: true,
    errors: true,
  },
};

const MAX_EXPANSION_ATTEMPTS = 20;
const INITIAL_DELAY_MS = 200;
const DEBOUNCE_DELAY_MS = 100;

interface AsyncApiViewerProps {
  schema: IAsyncApiSpecification['specification'];
  config?: Partial<ConfigInterface>;
}

function AsyncApiViewer({ schema, config = {} }: AsyncApiViewerProps) {
  const { cx, classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const mutationCountRef = useRef(0);

  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  useEffect(() => {
    let debounceTimer: NodeJS.Timeout | null = null;

    const handleInitialExpansion = () => {
      mutationCountRef.current = 0;
      expandAllHidden(containerRef, mutationCountRef.current++);
    };

    const handleMutations = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        expandAllHidden(containerRef, mutationCountRef.current++);
      }, DEBOUNCE_DELAY_MS);
    };

    const timer = setTimeout(handleInitialExpansion, INITIAL_DELAY_MS);

    const observer = new MutationObserver(handleMutations);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timer);
      if (debounceTimer) clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [schema]);

  return (
    <div className={cx(classes.asyncapiViewer)} ref={containerRef}>
      <ErrorBoundary>
        <AsyncApiComponent schema={schema} config={mergedConfig} />
      </ErrorBoundary>
    </div>
  );
}

function findExpandButton(element: Element): HTMLButtonElement | null {
  let sibling: Element | null = element.previousElementSibling;
  while (sibling) {
    const button = sibling.querySelector('button:first-child') as HTMLButtonElement | null;
    if (button) return button;
    sibling = sibling.previousElementSibling;
  }
  return null;
}

function expandAllHidden(
  containerRef: React.RefObject<HTMLDivElement>,
  mutationCount: number,
): boolean {
  if (!containerRef.current || mutationCount >= MAX_EXPANSION_ATTEMPTS) {
    return false;
  }

  const hiddenDivs = containerRef.current.querySelectorAll('div.hidden');
  if (hiddenDivs.length === 0) {
    return false;
  }

  let hasExpanded = false;
  hiddenDivs.forEach((hiddenDiv) => {
    const button = findExpandButton(hiddenDiv);
    if (button) {
      button.click();
      hasExpanded = true;
    }
  });

  return hasExpanded;
}

export { AsyncApiViewer };
